import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XtreamContent } from '../../api/xtreamAPI';
import { getCatalogAlternatives } from '../../api/backendAPI';
import { useDevice } from '../../contexts/DeviceContext';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import './AlternativesModal.css';

interface AlternativesModalProps {
  /** Original name of the current item (as displayed) */
  currentName: string;
  /** The Xtream stream_id or series_id of the currently open item (to highlight/exclude) */
  currentStreamId: string;
  type: 'movie' | 'series';
  onSelect: (item: XtreamContent) => void;
  onClose: () => void;
}

export const AlternativesModal: React.FC<AlternativesModalProps> = ({
  currentName,
  currentStreamId,
  type,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { deviceId, activationPassword } = useDevice();
  const [items, setItems] = useState<XtreamContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [focusedIdx, setFocusedIdx] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // ── Load alternatives from cache on mount ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const alternatives = await getCatalogAlternatives(
          deviceId,
          activationPassword,
          type,
          currentName,
          currentStreamId,
          consumeCatalogForceRefresh(`alternatives-${type}`),
        );
        if (cancelled) return;
        const matches: XtreamContent[] = alternatives.map((item) => ({
          stream_id: type === 'movie' ? item.streamId : undefined,
          series_id: type === 'series' ? item.streamId : undefined,
          name: item.title,
          stream_icon: item.posterUrl || undefined,
          cover: item.posterUrl || undefined,
          category_id: item.categoryId,
          stream_type: type,
        }));

        // Put current first
        matches.sort((a, b) => {
          const aIsCurrent = (a.stream_id ?? a.series_id) === currentStreamId ? -1 : 0;
          const bIsCurrent = (b.stream_id ?? b.series_id) === currentStreamId ? -1 : 0;
          return aIsCurrent - bIsCurrent;
        });

        setItems(matches);
        // Focus current item
        const currentIdx = matches.findIndex(
          (s) => (s.stream_id ?? s.series_id) === currentStreamId,
        );
        setFocusedIdx(currentIdx >= 0 ? currentIdx : 0);
      } catch {
        setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [activationPassword, currentName, currentStreamId, deviceId, type]);

  // ── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const code = e.keyCode;
      if (key === 'Escape' || key === 'GoBack' || key === 'BrowserBack' || code === 27 || code === 10009 || code === 461) {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      } else if (key === 'ArrowDown' || key === 'Down' || code === 40) {
        e.preventDefault();
        e.stopPropagation();
        setFocusedIdx((p) => Math.min(p + 1, items.length - 1));
      } else if (key === 'ArrowUp' || key === 'Up' || code === 38) {
        e.preventDefault();
        e.stopPropagation();
        setFocusedIdx((p) => Math.max(p - 1, 0));
      } else if (key === 'Enter' || key === ' ' || code === 13) {
        e.preventDefault();
        e.stopPropagation();
        const item = items[focusedIdx];
        if (item) onSelect(item);
      } else {
        // Block all other keys from reaching the parent
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true); // capture phase
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [items, focusedIdx, onClose, onSelect]);

  // ── Scroll focused item into view ────────────────────────────────────────
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const row = list.children[focusedIdx] as HTMLElement | undefined;
    if (!row) return;
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < list.scrollTop) {
      list.scrollTop = rowTop - 8;
    } else if (rowBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = rowBottom - list.clientHeight + 8;
    }
  }, [focusedIdx]);

  return (
    <div className="alts-modal" onClick={onClose}>
      <div className="alts-modal__container" onClick={(e) => e.stopPropagation()}>
        <p className="alts-modal__title">{t('alternatives.title')}</p>

        {loading ? (
          <div className="alts-modal__loading">{t('common.loading')}</div>
        ) : items.length === 0 ? (
          <div className="alts-modal__empty">{t('alternatives.empty')}</div>
        ) : (
          <div className="alts-modal__list" ref={listRef}>
            {items.map((item, i) => {
              const sid = item.stream_id ?? item.series_id ?? '';
              const isCurrent = sid === currentStreamId;
              const poster = item.stream_icon || item.cover || item.icon || item.tvg_logo || '';
              return (
                <div
                  key={sid || i}
                  className={[
                    'alts-modal__item',
                    isCurrent ? 'alts-modal__item--current' : '',
                    i === focusedIdx ? 'alts-modal__item--focused' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => onSelect(item)}
                >
                  <div className="alts-modal__poster">
                    {poster && (
                      <img
                        src={poster}
                        alt=""
                        className="alts-modal__poster-img"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <span className="alts-modal__name">{item.name}</span>
                  {isCurrent && (
                    <span className="alts-modal__current-badge">{t('alternatives.current')}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
